// API Service for CERAD Backend

import { getApiUrl } from './urls';

const API_BASE_URL = getApiUrl();
if (process.env.NODE_ENV === 'development') {
  console.log('🚀 [API] Base URL:', API_BASE_URL);
}

import {
  ApiResponse,
  User,
  AuthResponse,
  Article,
  Formation,
  Reference,
  ContactRequest,
  DashboardStats,
  ActivityItem,
  TodayAnalytics,
  AnalyticsOverview,
  Partner,
  Testimonial,
  PartnerWithTranslations,
  TestimonialWithTranslations,
  Leader,
  UserListItem,
  CreateUserRequest,
  UpdateUserRequest,
  Notification,
  NotificationListResponse
} from '@shared/types';

export type {
  ApiResponse,
  User,
  AuthResponse,
  Article,
  Formation,
  Reference,
  ContactRequest,
  DashboardStats,
  ActivityItem,
  TodayAnalytics,
  AnalyticsOverview,
  UserListItem,
  CreateUserRequest,
  UpdateUserRequest,
  Notification,
  NotificationListResponse
};

// Token management
export const getToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('cerad_token');
};

export const setToken = (token: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('cerad_token', token);
  }
};

export const removeToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('cerad_token');
    localStorage.removeItem('cerad_user');
  }
};

export const getStoredUser = (): User | null => {
  if (typeof window === 'undefined') return null;
  const user = localStorage.getItem('cerad_user');
  return user ? JSON.parse(user) : null;
};

export const setStoredUser = (user: User): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('cerad_user', JSON.stringify(user));
  }
};

// API fetch wrapper
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    const data = await response.json();
    
    if (!response.ok) {
      // Extract error message from various possible formats
      const errorMessage = data.error?.message || data.message || 'Une erreur est survenue';
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    // If it's an error we threw ourselves (server returned error response)
    if (error instanceof Error && !error.message.includes('Unexpected token')) {
      return {
        success: false,
        message: error.message,
        data: null as unknown as T,
      };
    }

    // Real network or parsing error
    console.error(`API Network Error [${endpoint}]:`, error);
    return {
      success: false,
      message: 'Le serveur est actuellement indisponible ou une erreur réseau est survenue',
      data: null as unknown as T,
    };
  }
}

// Auth API
export const authApi = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await apiFetch<{ token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.data) {
      setToken(response.data.token);
      setStoredUser(response.data.user);
    }

    return response as AuthResponse;
  },

  logout: async (): Promise<void> => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch (e) {
      console.error(e);
    }
    removeToken();
  },

  getMe: async (): Promise<ApiResponse<User>> => {
    return apiFetch<User>('/auth/me');
  },

  updateProfile: async (data: Partial<User>): Promise<ApiResponse<User>> => {
    const response = await apiFetch<User>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (response.success && response.data) {
      setStoredUser(response.data);
    }
    return response;
  },

  updatePassword: async (currentPassword: string, newPassword: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },
};

// Articles API

export const articlesApi = {
  getAll: async (params?: Record<string, string>): Promise<ApiResponse<Article[]>> => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiFetch<Article[]>(`/articles${query}`);
  },

  getBySlug: async (slug: string): Promise<ApiResponse<Article>> => {
    return apiFetch<Article>(`/articles/${slug}`);
  },

  create: async (data: Partial<Article>): Promise<ApiResponse<Article>> => {
    return apiFetch<Article>('/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Article>): Promise<ApiResponse<Article>> => {
    return apiFetch<Article>(`/articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/articles/${id}`, {
      method: 'DELETE',
    });
  },

  togglePublish: async (id: number): Promise<ApiResponse<{ published: boolean; status: string }>> => {
    return apiFetch<{ published: boolean; status: string }>(`/articles/${id}/publish`, {
      method: 'PATCH',
    });
  },

  updateStatus: async (id: number, status: 'draft' | 'published' | 'archived'): Promise<ApiResponse<{ status: string; published: boolean }>> => {
    return apiFetch<{ status: string; published: boolean }>(`/articles/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
};

// Formations API

export const formationsApi = {
  getAll: async (params?: Record<string, string>): Promise<ApiResponse<Formation[]>> => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiFetch<Formation[]>(`/formations${query}`);
  },

  getById: async (id: number): Promise<ApiResponse<Formation>> => {
    return apiFetch<Formation>(`/formations/${id}`);
  },

  create: async (data: Partial<Formation>): Promise<ApiResponse<Formation>> => {
    return apiFetch<Formation>('/formations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Formation>): Promise<ApiResponse<Formation>> => {
    return apiFetch<Formation>(`/formations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/formations/${id}`, {
      method: 'DELETE',
    });
  },

  togglePublish: async (id: number): Promise<ApiResponse<{ published: boolean }>> => {
    return apiFetch<{ published: boolean }>(`/formations/${id}/publish`, {
      method: 'PATCH',
    });
  },
};

// References API

export const referencesApi = {
  getAll: async (params?: Record<string, string>): Promise<ApiResponse<Reference[]>> => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiFetch<Reference[]>(`/references${query}`);
  },

  getBySlug: async (slug: string): Promise<ApiResponse<Reference>> => {
    return apiFetch<Reference>(`/references/${slug}`);
  },

  create: async (data: Partial<Reference>): Promise<ApiResponse<Reference>> => {
    return apiFetch<Reference>('/references', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Reference>): Promise<ApiResponse<Reference>> => {
    return apiFetch<Reference>(`/references/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/references/${id}`, {
      method: 'DELETE',
    });
  },

  togglePublish: async (id: number): Promise<ApiResponse<{ published: boolean }>> => {
    return apiFetch<{ published: boolean }>(`/references/${id}/publish`, {
      method: 'PATCH',
    });
  },
};

// Contact API

export const contactApi = {
  submit: async (data: {
    name: string;
    email: string;
    phone?: string;
    organization?: string;
    subject: string;
    message: string;
    type?: 'CONTACT' | 'ETUDES_RECHERCHE' | 'SUIVI_EVALUATION' | 'CONSEIL_RSE' | 'FORMATION' | 'AUTRE';
    projectScope?: string;
    budgetRange?: string;
    preferredTimeline?: string;
    estimatedDuration?: string;
  }): Promise<ApiResponse<{ id: number }>> => {
    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      company: data.organization,
      subject: data.subject,
      message: data.message,
      type: data.type || 'CONTACT',
      projectScope: data.projectScope,
      budgetRange: data.budgetRange,
      preferredTimeline: data.preferredTimeline,
      estimatedDuration: data.estimatedDuration,
    };
    return apiFetch<{ id: number }>('/contact', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getAll: async (params?: Record<string, string>): Promise<ApiResponse<ContactRequest[]>> => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiFetch<ContactRequest[]>(`/contact${query}`);
  },

  updateStatus: async (id: number, status: string): Promise<ApiResponse<ContactRequest>> => {
    return apiFetch<ContactRequest>(`/contact/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/contact/${id}`, {
      method: 'DELETE',
    });
  },
};

// Dashboard API

export const dashboardApi = {
  getStats: async (): Promise<ApiResponse<DashboardStats>> => {
    return apiFetch<DashboardStats>('/dashboard/stats');
  },

  getActivity: async (): Promise<ApiResponse<ActivityItem[]>> => {
    return apiFetch<ActivityItem[]>('/dashboard/activity');
  },
};

// Analytics API
export const analyticsApi = {
  getToday: async (): Promise<ApiResponse<TodayAnalytics>> => {
    return apiFetch<TodayAnalytics>('/analytics/today');
  },

  getOverview: async (days: number = 7): Promise<ApiResponse<AnalyticsOverview>> => {
    return apiFetch<AnalyticsOverview>(`/analytics/overview?days=${days}`);
  },
};

// Storage API
export interface StorageStats {
  total: {
    size: number;
    sizeFormatted: string;
    files: number;
  };
  directories: {
    images?: { size: number; sizeFormatted: string; files: number };
    temp?: { size: number; sizeFormatted: string; files: number };
    // Legacy fields for backward compatibility
    originals?: { size: number; sizeFormatted: string; files: number };
    processed?: { size: number; sizeFormatted: string; files: number };
  };
  fileTypes: Array<{
    type: string;
    count: number;
    size: number;
    sizeFormatted: string;
  }>;
  recentFiles: Array<{
    name: string;
    size: number;
    sizeFormatted: string;
    date: string;
    path: string;
  }>;
}

export const storageApi = {
  getStats: async (): Promise<ApiResponse<StorageStats>> => {
    return apiFetch<StorageStats>('/upload/storage');
  },
};

// Media Library API
export interface MediaItem {
  id: number;
  url: string;
  filename: string;
  filepath?: string;
  mimetype?: string;
  filesize?: number;
  width?: number | null;
  height?: number | null;
  size?: number;
  sizeFormatted?: string;
  format?: string;
  alt: string | null;
  category: string;
  mediaType?: 'image' | 'video' | 'document';
  publicationId?: number | null;
  createdAt: string;
}

export interface MediaListResponse {
  media: MediaItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface MediaUsageInfo {
  isUsed: boolean;
  articles: Array<{ id: number; title: string; slug: string }>;
}

export interface BulkDeleteResult {
  deleted: number[];
  failed: number[];
  used: number[];
}

const API_URL = getApiUrl();

export const mediaApi = {
  getAll: async (params?: Record<string, string>): Promise<ApiResponse<MediaListResponse>> => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiFetch<MediaListResponse>(`/upload/media${query}`);
  },

  update: async (id: number, data: { alt?: string; category?: string }): Promise<ApiResponse<MediaItem>> => {
    return apiFetch<MediaItem>(`/upload/media/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number, force?: boolean): Promise<ApiResponse<null>> => {
    const query = force ? '?force=true' : '';
    return apiFetch<null>(`/upload/media/${id}${query}`, {
      method: 'DELETE',
    });
  },

  bulkDelete: async (ids: number[], force?: boolean): Promise<ApiResponse<BulkDeleteResult>> => {
    return apiFetch<BulkDeleteResult>('/upload/media/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids, force }),
    });
  },

  getUsage: async (id: number): Promise<ApiResponse<MediaUsageInfo>> => {
    return apiFetch<MediaUsageInfo>(`/upload/media/${id}/usage`);
  },

  uploadImage: async (
    file: File,
    options?: { alt?: string; category?: string; publicationId?: number },
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<MediaItem>> => {
    const token = getToken();
    const formData = new FormData();
    formData.append('image', file);
    if (options?.alt) formData.append('alt', options.alt);
    if (options?.category) formData.append('category', options.category);
    if (options?.publicationId) formData.append('publicationId', String(options.publicationId));

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/upload/media`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 400) {
            const msg = data.error?.message || data.message || `Erreur ${xhr.status}`;
            reject(new Error(msg));
          } else {
            resolve(data);
          }
        } catch {
          reject(new Error('Réponse invalide du serveur'));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(formData);
    });
  },

  uploadVideo: async (
    file: File,
    options?: { alt?: string; category?: string; publicationId?: number },
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<MediaItem>> => {
    const token = getToken();
    const formData = new FormData();
    formData.append('video', file);
    if (options?.alt) formData.append('alt', options.alt);
    if (options?.category) formData.append('category', options.category || 'video');
    if (options?.publicationId) formData.append('publicationId', String(options.publicationId));

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/upload/video`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 400) {
            const msg = data.error?.message || data.message || `Erreur ${xhr.status}`;
            reject(new Error(msg));
          } else {
            resolve(data);
          }
        } catch {
          reject(new Error('Réponse invalide du serveur'));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(formData);
    });
  },

  uploadDocument: async (
    file: File,
    options?: { alt?: string; category?: string; publicationId?: number }
  ): Promise<ApiResponse<MediaItem>> => {
    const token = getToken();
    const formData = new FormData();
    formData.append('document', file);
    if (options?.alt) formData.append('alt', options.alt);
    if (options?.category) formData.append('category', options.category || 'document');
    if (options?.publicationId) formData.append('publicationId', String(options.publicationId));

    const response = await fetch(`${API_URL}/upload/document`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    return response.json();
  },

  replace: async (id: number, file: File): Promise<ApiResponse<MediaItem>> => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/upload/media/${id}/replace`, {
      method: 'PUT',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    return response.json();
  },

  getStats: async (): Promise<ApiResponse<{
    totalSize: number;
    totalSizeFormatted: string;
    totalFiles: number;
    byType: Array<{ type: string; count: number; size: number; sizeFormatted: string }>;
  }>> => {
    return apiFetch('/upload/stats');
  },
};

// Site Configuration API
export interface SiteConfig {
  id: number;
  companyName: string;
  companyFullName: string;
  slogan: string;
  description: string;
  descriptionLong: string | null;
  phone: string;
  whatsapp: string | null;
  email: string;
  address: string;
  city: string;
  country: string;
  mapLatitude: number | null;
  mapLongitude: number | null;
  mapZoom: number | null;
  openingHours: string;
  facebook: string | null;
  linkedin: string | null;
  twitter: string | null;
  instagram: string | null;
  youtube: string | null;
  tiktok: string | null;
  yearsExperience: number;
  projectsCount: number;
  partnersCount: number;
  founderName: string;
  founderTitle: string;
  founderBio: string | null;
  founderImage: string | null;
  partners: string[];
  siteUrl: string;
  apiUrl: string;
  metaTitle: string | null;
  metaDescription: string | null;
  updatedAt: string;
  legalPage_sections_fr?: LegalSection[];
  legalPage_sections_en?: LegalSection[];
  legalPage_lastUpdated_fr?: string;
  legalPage_lastUpdated_en?: string;
  privacyPage_sections_fr?: LegalSection[];
  privacyPage_sections_en?: LegalSection[];
  privacyPage_lastUpdated_fr?: string;
  privacyPage_lastUpdated_en?: string;
}

export interface LegalSection {
  title: string;
  body: string;
}

// Contact specific type
export interface ContactConfig {
  phone: string;
  whatsapp: string | null;
  email: string;
  address: string;
  city: string;
  country: string;
  mapLatitude: number | null;
  mapLongitude: number | null;
  mapZoom: number | null;
  openingHours: string;
  facebook: string | null;
  linkedin: string | null;
  twitter: string | null;
  instagram: string | null;
  youtube: string | null;
  tiktok: string | null;
}

export const settingsApi = {
  get: async (): Promise<ApiResponse<SiteConfig>> => {
    return apiFetch<SiteConfig>('/settings');
  },

  update: async (data: Partial<SiteConfig>): Promise<ApiResponse<SiteConfig>> => {
    // Use PATCH for bulk update with new key-value system
    // Remove id and updatedAt as they're not settings keys
    const settings = { ...(data as Partial<SiteConfig>) } as Record<string, unknown>;
    delete settings.id;
    delete settings.updatedAt;
    return apiFetch<SiteConfig>('/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  },

  getStats: async (): Promise<ApiResponse<Pick<SiteConfig, 'yearsExperience' | 'projectsCount' | 'partnersCount'>>> => {
    return apiFetch<Pick<SiteConfig, 'yearsExperience' | 'projectsCount' | 'partnersCount'>>('/settings/stats');
  },

  getContact: async (): Promise<ApiResponse<ContactConfig>> => {
    return apiFetch<ContactConfig>('/settings/contact');
  },
};

// Categories API
export interface Category {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
  articleCount?: number;
  createdAt: string;
  updatedAt: string;
}

export const categoriesApi = {
  getAll: async (): Promise<ApiResponse<Category[]>> => {
    return apiFetch<Category[]>('/categories');
  },

  getBySlug: async (slug: string): Promise<ApiResponse<Category>> => {
    return apiFetch<Category>(`/categories/${slug}`);
  },

  create: async (data: Partial<Category>): Promise<ApiResponse<Category>> => {
    return apiFetch<Category>('/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Category>): Promise<ApiResponse<Category>> => {
    return apiFetch<Category>(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/categories/${id}`, {
      method: 'DELETE',
    });
  },
};

// Tags API
export interface Tag {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  color: string | null;
  articleCount?: number;
  createdAt: string;
}

export const tagsApi = {
  getAll: async (): Promise<ApiResponse<Tag[]>> => {
    return apiFetch<Tag[]>('/tags');
  },

  getBySlug: async (slug: string): Promise<ApiResponse<Tag>> => {
    return apiFetch<Tag>(`/tags/${slug}`);
  },

  create: async (data: Partial<Tag>): Promise<ApiResponse<Tag>> => {
    return apiFetch<Tag>('/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  createBulk: async (names: string[]): Promise<ApiResponse<{ created: Tag[]; existing: Tag[] }>> => {
    return apiFetch<{ created: Tag[]; existing: Tag[] }>('/tags/bulk', {
      method: 'POST',
      body: JSON.stringify({ names }),
    });
  },

  update: async (id: number, data: Partial<Tag>): Promise<ApiResponse<Tag>> => {
    return apiFetch<Tag>(`/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/tags/${id}`, {
      method: 'DELETE',
    });
  },
};

// Partners & Testimonials API

export const partnersApi = {
  getAll: async (activeOnly = true, locale = 'fr'): Promise<ApiResponse<Partner[]>> => {
    const params = new URLSearchParams();
    if (activeOnly) params.append('active', 'true');
    params.append('locale', locale);
    return apiFetch<Partner[]>(`/partners?${params.toString()}`);
  },

  getAllAdmin: async (activeOnly?: boolean): Promise<ApiResponse<PartnerWithTranslations[]>> => {
    const params = new URLSearchParams();
    if (activeOnly !== undefined) params.append('active', activeOnly.toString());
    return apiFetch<PartnerWithTranslations[]>(`/partners/admin/all?${params.toString()}`);
  },

  getById: async (id: number, locale = 'fr'): Promise<ApiResponse<Partner>> => {
    return apiFetch<Partner>(`/partners/${id}?locale=${locale}`);
  },

  create: async (data: Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Partner>> => {
    return apiFetch<Partner>('/partners', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Partner>): Promise<ApiResponse<Partner>> => {
    return apiFetch<Partner>(`/partners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateByCode: async (code: string, data: Partial<Partner>): Promise<ApiResponse<Partner>> => {
    return apiFetch<Partner>(`/partners/${code}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/partners/${id}`, {
      method: 'DELETE',
    });
  },

  deleteByCode: async (code: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/partners/${code}`, {
      method: 'DELETE',
    });
  },

  toggle: async (id: number): Promise<ApiResponse<Partner>> => {
    return apiFetch<Partner>(`/partners/${id}/toggle`, {
      method: 'PATCH',
    });
  },

  toggleByCode: async (code: string): Promise<ApiResponse<Partner>> => {
    return apiFetch<Partner>(`/partners/${code}/toggle`, {
      method: 'PATCH',
    });
  },
};

export const testimonialsApi = {
  getAll: async (activeOnly = true, locale = 'fr'): Promise<ApiResponse<Testimonial[]>> => {
    const params = new URLSearchParams();
    if (activeOnly) params.append('active', 'true');
    params.append('locale', locale);
    return apiFetch<Testimonial[]>(`/testimonials?${params.toString()}`);
  },

  getAllAdmin: async (activeOnly?: boolean): Promise<ApiResponse<TestimonialWithTranslations[]>> => {
    const params = new URLSearchParams();
    if (activeOnly !== undefined) params.append('active', activeOnly.toString());
    return apiFetch<TestimonialWithTranslations[]>(`/testimonials/admin/all?${params.toString()}`);
  },

  getById: async (id: number, locale = 'fr'): Promise<ApiResponse<Testimonial>> => {
    return apiFetch<Testimonial>(`/testimonials/${id}?locale=${locale}`);
  },

  create: async (data: Omit<Testimonial, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Testimonial>> => {
    return apiFetch<Testimonial>('/testimonials', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Testimonial>): Promise<ApiResponse<Testimonial>> => {
    return apiFetch<Testimonial>(`/testimonials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateByCode: async (code: string, data: Partial<Testimonial>): Promise<ApiResponse<Testimonial>> => {
    return apiFetch<Testimonial>(`/testimonials/${code}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/testimonials/${id}`, {
      method: 'DELETE',
    });
  },

  deleteByCode: async (code: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/testimonials/${code}`, {
      method: 'DELETE',
    });
  },

  toggle: async (id: number): Promise<ApiResponse<Testimonial>> => {
    return apiFetch<Testimonial>(`/testimonials/${id}/toggle`, {
      method: 'PATCH',
    });
  },

  toggleByCode: async (code: string): Promise<ApiResponse<Testimonial>> => {
    return apiFetch<Testimonial>(`/testimonials/${code}/toggle`, {
      method: 'PATCH',
    });
  },
};

// Leaders API
export const leadersApi = {
  getAll: async (activeOnly = true): Promise<ApiResponse<Leader[]>> => {
    const params = new URLSearchParams();
    if (activeOnly) params.append('active', 'true');
    return apiFetch<Leader[]>(`/leaders?${params.toString()}`);
  },

  getAllAdmin: async (): Promise<ApiResponse<Leader[]>> => {
    return apiFetch<Leader[]>('/leaders/admin');
  },

  getById: async (id: number): Promise<ApiResponse<Leader>> => {
    return apiFetch<Leader>(`/leaders/${id}`);
  },

  create: async (data: Omit<Leader, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Leader>> => {
    return apiFetch<Leader>('/leaders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Leader>): Promise<ApiResponse<Leader>> => {
    return apiFetch<Leader>(`/leaders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/leaders/${id}`, {
      method: 'DELETE',
    });
  },

  toggle: async (id: number): Promise<ApiResponse<Leader>> => {
    return apiFetch<Leader>(`/leaders/${id}/toggle`, {
      method: 'PATCH',
    });
  },
};

// Formateurs API
export interface Formateur {
  id: number;
  nom: string;
  titre: string;
  bio?: string | null;
  photo?: string | null;
  email?: string | null;
  telephone?: string | null;
  linkedin?: string | null;
  specialites?: string[];
  isActive: boolean;
  formationsCount?: number;
  createdAt: string;
  updatedAt: string;
}

export const formateursApi = {
  getAll: async (activeOnly = false): Promise<ApiResponse<Formateur[]>> => {
    const params = new URLSearchParams();
    if (activeOnly) params.append('active', 'true');
    return apiFetch<Formateur[]>(`/formateurs?${params.toString()}`);
  },

  getById: async (id: number): Promise<ApiResponse<Formateur>> => {
    return apiFetch<Formateur>(`/formateurs/${id}`);
  },

  create: async (data: Omit<Formateur, 'id' | 'createdAt' | 'updatedAt' | 'formationsCount'>): Promise<ApiResponse<Formateur>> => {
    return apiFetch<Formateur>('/formateurs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: Partial<Formateur>): Promise<ApiResponse<Formateur>> => {
    return apiFetch<Formateur>(`/formateurs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/formateurs/${id}`, {
      method: 'DELETE',
    });
  },
};

// Newsletter / Subscriber API
export interface Subscriber {
  id: number;
  email: string;
  source: string;
  isActive: boolean;
  confirmedAt?: string | null;
  unsubscribedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}


export interface NewsletterStats {
  total: number;
  active: number;
  unsubscribed: number;
  thisMonth: number;
  bySource: { source: string; count: number }[];
}

export const newsletterApi = {
  // Public: Subscribe to newsletter
  subscribe: async (email: string, source = 'footer'): Promise<ApiResponse<{ message: string; alreadySubscribed?: boolean; resubscribed?: boolean }>> => {
    return apiFetch<{ message: string; alreadySubscribed?: boolean; resubscribed?: boolean }>('/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email, source }),
    });
  },

  // Public: Unsubscribe from newsletter
  unsubscribe: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    return apiFetch<{ message: string }>('/newsletter/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Admin: Get all subscribers with pagination
  getAll: async (params?: { page?: number; limit?: number; active?: boolean; search?: string }): Promise<ApiResponse<Subscriber[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.active !== undefined) queryParams.append('active', params.active.toString());
    if (params?.search) queryParams.append('search', params.search);
    return apiFetch<Subscriber[]>(`/newsletter/subscribers?${queryParams.toString()}`);
  },

  // Admin: Get newsletter stats
  getStats: async (): Promise<ApiResponse<NewsletterStats>> => {
    return apiFetch<NewsletterStats>('/newsletter/stats');
  },

  // Admin: Delete subscriber
  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/newsletter/subscribers/${id}`, {
      method: 'DELETE',
    });
  },
};

// ==========================================
// User Management API (Admin only)
// ==========================================
export const usersApi = {
  getAll: async (): Promise<ApiResponse<UserListItem[]>> => {
    return apiFetch<UserListItem[]>('/auth/users');
  },

  create: async (data: CreateUserRequest): Promise<ApiResponse<UserListItem>> => {
    return apiFetch<UserListItem>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: UpdateUserRequest): Promise<ApiResponse<UserListItem>> => {
    return apiFetch<UserListItem>(`/auth/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/auth/users/${id}`, {
      method: 'DELETE',
    });
  },

  getPermissions: async (): Promise<ApiResponse<Array<{
    code: string;
    name: string;
    description: string;
  }>>> => {
    return apiFetch('/auth/permissions');
  },
};

// ==========================================
// Notifications API
// ==========================================
export const notificationsApi = {
  getAll: async (params?: { page?: number; limit?: number; unreadOnly?: boolean }): Promise<ApiResponse<NotificationListResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.unreadOnly) queryParams.append('unreadOnly', 'true');
    return apiFetch<NotificationListResponse>(`/notifications?${queryParams.toString()}`);
  },

  getUnreadCount: async (): Promise<ApiResponse<{ count: number }>> => {
    return apiFetch<{ count: number }>('/notifications/unread-count');
  },

  markAsRead: async (id: number): Promise<ApiResponse<Notification>> => {
    return apiFetch<Notification>(`/notifications/${id}/read`, {
      method: 'PATCH'
    });
  },

  markAllAsRead: async (): Promise<ApiResponse<{ count: number }>> => {
    return apiFetch<{ count: number }>('/notifications/mark-all-read', {
      method: 'PATCH'
    });
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/notifications/${id}`, {
      method: 'DELETE'
    });
  },

  clearRead: async (): Promise<ApiResponse<{ count: number }>> => {
    return apiFetch<{ count: number }>('/notifications/clear-read', {
      method: 'DELETE'
    });
  }
};

// ─────────────────────────────────────────────
// Diagnostic Module Types
// ─────────────────────────────────────────────

export interface DiagnosticOption {
  id: number;
  questionId: number;
  label: string;
  value: string;
  score: number;
  order: number;
  comment: string | null;
}

export interface DiagnosticQuestion {
  id: number;
  diagnosticId: number;
  type: 'SINGLE_CHOICE' | 'MULTI_CHOICE' | 'TEXT' | 'TEXTAREA' | 'RATING' | 'YES_NO' | 'SCALE';
  label: string;
  description: string | null;
  group: string | null;
  subGroup: string | null;
  order: number;
  weight: number;
  isRequired: boolean;
  maxScore: number | null;
  options: DiagnosticOption[];
}

export interface DiagnosticSummary {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  category: string;
  isPublished: boolean;
  isArchived: boolean;
  createdAt: string;
  _count?: { questions: number; sessions: number };
}

export interface DiagnosticFull extends DiagnosticSummary {
  questions: DiagnosticQuestion[];
}

export interface DiagnosticSession {
  id: number;
  orgName: string;
  orgType: string | null;
  orgCity: string | null;
  orgEmail: string | null;
  percentage: number | null;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED';
  completedAt: string | null;
  createdAt: string;
}

export interface DiagnosticResult {
  totalScore: number;
  maxScore: number;
  percentage: number;
  scoreByGroup: Record<string, number>;
}

export interface DiagnosticGlobalStats {
  total: number;
  completed: number;
  inProgress: number;
  avgScore: number;
  completionRate: number;
  distribution: Array<{ range: string; count: number }>;
  submissionsPerDay: Record<string, number>;
}

// ─────────────────────────────────────────────
// Diagnostic API
// ─────────────────────────────────────────────

export const diagnosticApi = {
  // Admin — list all diagnostics
  getAll: async (params?: { archived?: boolean }): Promise<ApiResponse<DiagnosticSummary[]>> => {
    const query = params?.archived ? '?archived=true' : '';
    return apiFetch<DiagnosticSummary[]>(`/diagnostics${query}`);
  },

  // Public — get diagnostic by slug with questions
  getBySlug: async (slug: string): Promise<ApiResponse<DiagnosticFull>> => {
    return apiFetch<DiagnosticFull>(`/diagnostics/${slug}`);
  },

  // Admin — create
  create: async (data: { title: string; slug: string; description?: string; category?: string }): Promise<ApiResponse<DiagnosticSummary>> => {
    return apiFetch<DiagnosticSummary>('/diagnostics', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin — update
  update: async (id: number, data: Partial<DiagnosticSummary>): Promise<ApiResponse<DiagnosticSummary>> => {
    return apiFetch<DiagnosticSummary>(`/diagnostics/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Admin — publish / unpublish
  setPublished: async (id: number, isPublished: boolean): Promise<ApiResponse<DiagnosticSummary>> => {
    return apiFetch<DiagnosticSummary>(`/diagnostics/${id}/publish`, {
      method: 'PATCH',
      body: JSON.stringify({ isPublished }),
    });
  },

  // Admin — archive / unarchive
  setArchived: async (id: number, isArchived: boolean): Promise<ApiResponse<DiagnosticSummary>> => {
    return apiFetch<DiagnosticSummary>(`/diagnostics/${id}/archive`, {
      method: 'PATCH',
      body: JSON.stringify({ isArchived }),
    });
  },

  // Admin — delete
  delete: async (id: number): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/diagnostics/${id}`, { method: 'DELETE' });
  },

  // Admin — import from JSON
  importJson: async (json: unknown): Promise<ApiResponse<{ id: number; slug: string; title: string; questionCount: number }>> => {
    return apiFetch('/diagnostic-import', {
      method: 'POST',
      body: JSON.stringify(json),
    });
  },

  // Admin — export to JSON
  exportJson: async (id: number): Promise<Blob> => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/diagnostic-import/${id}/export`, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      credentials: 'include',
    });
    return response.blob();
  },

  // Public — start session
  startSession: async (slug: string, orgInfo: {
    orgName: string;
    orgType?: string;
    orgCity?: string;
    orgPhone?: string;
    orgEmail?: string;
    orgSector?: string;
  }): Promise<ApiResponse<{ sessionId: number }>> => {
    return apiFetch<{ sessionId: number }>(`/diagnostic-sessions/${slug}/start`, {
      method: 'POST',
      body: JSON.stringify(orgInfo),
    });
  },

  // Public — save answers
  saveAnswers: async (sessionId: number, answers: Array<{
    questionId: number;
    valueText?: string;
    valueNumber?: number;
    valueOptions?: number[];
  }>): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/diagnostic-sessions/${sessionId}/answers`, {
      method: 'PATCH',
      body: JSON.stringify({ answers }),
    });
  },

  // Public — complete session and calculate score
  completeSession: async (sessionId: number): Promise<ApiResponse<DiagnosticResult>> => {
    return apiFetch<DiagnosticResult>(`/diagnostic-sessions/${sessionId}/complete`, {
      method: 'POST',
    });
  },

  // Public — get session result
  getResult: async (sessionId: number): Promise<ApiResponse<DiagnosticSession & { diagnostic: DiagnosticSummary; answers: unknown[]; scoreByGroup: Record<string, number> }>> => {
    return apiFetch(`/diagnostic-sessions/${sessionId}/result`);
  },

  // Admin — list sessions for a diagnostic
  getSessions: async (diagnosticId: number, params?: { status?: string; page?: number }): Promise<ApiResponse<DiagnosticSession[]>> => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.page) query.set('page', String(params.page));
    const qs = query.toString() ? `?${query.toString()}` : '';
    return apiFetch<DiagnosticSession[]>(`/diagnostic-sessions/diagnostic/${diagnosticId}${qs}`);
  },

  // Admin — global stats
  getStats: async (diagnosticId: number): Promise<ApiResponse<DiagnosticGlobalStats>> => {
    return apiFetch<DiagnosticGlobalStats>(`/diagnostic-stats/${diagnosticId}`);
  },

  // Admin — group stats
  getGroupStats: async (diagnosticId: number): Promise<ApiResponse<Array<{ group: string; avgScore: number }>>> => {
    return apiFetch(`/diagnostic-stats/${diagnosticId}/groups`);
  },

  // Admin — response distribution
  getResponseDistribution: async (diagnosticId: number): Promise<ApiResponse<unknown[]>> => {
    return apiFetch(`/diagnostic-stats/${diagnosticId}/responses`);
  },

  // Admin — questions CRUD
  questions: {
    getAll: async (diagnosticId: number): Promise<ApiResponse<DiagnosticQuestion[]>> => {
      return apiFetch<DiagnosticQuestion[]>(`/diagnostics/${diagnosticId}/questions`);
    },
    create: async (diagnosticId: number, data: Partial<DiagnosticQuestion>): Promise<ApiResponse<DiagnosticQuestion>> => {
      return apiFetch<DiagnosticQuestion>(`/diagnostics/${diagnosticId}/questions`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    update: async (diagnosticId: number, qId: number, data: Partial<DiagnosticQuestion>): Promise<ApiResponse<DiagnosticQuestion>> => {
      return apiFetch<DiagnosticQuestion>(`/diagnostics/${diagnosticId}/questions/${qId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    delete: async (diagnosticId: number, qId: number): Promise<ApiResponse<null>> => {
      return apiFetch<null>(`/diagnostics/${diagnosticId}/questions/${qId}`, { method: 'DELETE' });
    },
    reorder: async (diagnosticId: number, orders: Array<{ id: number; order: number }>): Promise<ApiResponse<null>> => {
      return apiFetch<null>(`/diagnostics/${diagnosticId}/questions/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ orders }),
      });
    },
  },
};
